import { Form as AntForm, Form, Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";

import { useTerminalSaveMutation } from "src/app/features/Terminal/services/server/TerminalApi";
import { updateWatchedFieldsUtils } from "src/app/features/Terminal/TerminalProfileToTerminalUtils";
import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import { useTerminalProfileLoadQuery } from "src/app/features/TerminalProfile/services/server/TerminalProfileApi";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showSuccessNotification } from "../../../../../components/NotificationAlerts";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";

export const TerminalCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("terminal");

    const [formData, setFormData] = useState<TerminalEntity>({} as TerminalEntity);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useTerminalSaveMutation();

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback(
        (newData: TerminalEntity) => {
            setFormData({
                ...newData,
                serialProtocolCheckReadOnly: formData.serialProtocolCheckReadOnly,
                posIpAddressCheckReadOnly: formData.posIpAddressCheckReadOnly,
                posPortCheckReadOnly: formData.posPortCheckReadOnly,
                deviceApiTokenCheckReadOnly: formData.deviceApiTokenCheckReadOnly,
                abortNeedsRequestIdCheckReadOnly: formData.abortNeedsRequestIdCheckReadOnly,
                cloudFlowEnabledCheckReadOnly: formData.cloudFlowEnabledCheckReadOnly,
            });
        },
        [formData],
    );

    const [form] = AntForm.useForm();
    const terminalProfileId = Form.useWatch("terminalProfileId", form);
    const [isNewTerminalProfileIdChanged, setIsNewTerminalProfileIdChanged] = useState<boolean>(false);

    const { data: terminalProfileEntity } = useTerminalProfileLoadQuery(
        { entityId: terminalProfileId },
        { skip: !terminalProfileId || !isNewTerminalProfileIdChanged },
    );

    // HANDLE WATCHED FIELDS
    const updateWatchedFields = useCallback(() => {
        if (!terminalProfileEntity || !setIsNewTerminalProfileIdChanged) return null;

        return updateWatchedFieldsUtils(terminalProfileEntity, formData);
    }, [terminalProfileEntity, formData]);

    useEffect(() => {
        if (!isNewTerminalProfileIdChanged || !terminalProfileEntity) {
            return;
        }
        let newFormData = updateWatchedFields();
        if (newFormData && !isTheSameByDeepCompare(formData, newFormData)) {
            setFormData(newFormData);
            newFormData = null;
            setIsNewTerminalProfileIdChanged(false);
        }
    }, [terminalProfileEntity, formData, isNewTerminalProfileIdChanged, updateWatchedFields]);

    useEffect(() => {
        if (terminalProfileId) setIsNewTerminalProfileIdChanged(true);
    }, [terminalProfileId]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "Terminal",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.TERMINALS}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<TerminalEntity>
                    name="TerminalCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                    customFormInstance={form}
                />
            )}
        </Spin>
    );
};
